import React, { useContext, useState } from 'react';
import { Container, Typography, Box, Paper, Divider, Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import { useSnackbar } from 'notistack';

const LearnerRulesPage = () => {
    const navigate = useNavigate();
    const auth = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const [submitting, setSubmitting] = useState(false);

    // Check if user is a learner and hasn't accepted rules yet
    const showAcceptButton = auth?.user?.role === 'learner' && !auth?.user?.hasAcceptedRules;

    const handleAccept = async () => {
        setSubmitting(true);
        try {
            await api.post('/api/users/accept-rules');
            await auth?.refreshUser();
            enqueueSnackbar('Règlement accepté avec succès', { variant: 'success' });
            navigate('/learner/dashboard');
        } catch (err) {
            console.error('Error accepting rules:', err);
            enqueueSnackbar('Erreur lors de l\'acceptation du règlement', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', py: 8 }}>
            <Container maxWidth="md">
                {!showAcceptButton && (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        sx={{ mb: 4, textTransform: 'none' }}
                    >
                        Retour
                    </Button>
                )}

                <Paper elevation={0} sx={{ p: { xs: 4, md: 6 }, borderRadius: 4, border: '1px solid #e2e8f0' }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, color: '#2d3748' }}>
                        Règlement Apprenant
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Règles de bonne conduite et obligations de l'apprenant
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 4 }}>
                            1. Assiduité et Engagement
                        </Typography>
                        <Typography variant="body1" paragraph>
                            L'apprenant s'engage à suivre le parcours de formation avec assiduité et à réaliser les activités pédagogiques et évaluations prévues. La progression est enregistrée et transmise à l'Organisme de Formation.
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 4 }}>
                            2. Comportement
                        </Typography>
                        <Typography variant="body1" paragraph>
                            L'apprenant s'engage à respecter les autres utilisateurs et les formateurs dans ses échanges sur les forums ou outils collaboratifs. Tout propos injurieux, discriminatoire ou déplacé est strictement interdit et sera reporté à l'Organisme de Formation qui pourra prendre les mesures appropriées.
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 4 }}>
                            3. Travail Personnel et Évaluations
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Les travaux et évaluations doivent être personnels. Toute forme de fraude ou de plagiat est passible de sanctions pouvant aller jusqu'à l'exclusion du parcours de formation.
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 4 }}>
                            4. Utilisation du Support
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Le support pédagogique est destiné à un usage strictement personnel. Toute diffusion en dehors du cadre de la formation est interdite sans autorisation préalable.
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 4 }}>
                            5. Préparation aux Certifications
                        </Typography>
                        <Typography variant="body1" paragraph>
                            L'apprenant doit respecter les modalités spécifiques de préparation aux certifications (délais, prérequis, dossiers à constituer), il peut se rapprocher de son formateur référent pour plus d'informations.
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 4 }}>
                            6. Signalement technique
                        </Typography>
                        <Typography variant="body1" paragraph>
                            En cas de difficulté technique empêchant le suivi de la formation, l'apprenant doit en informer le support dans les plus brefs délais via l'onglet dédié ou via l'adresse <a href="mailto:report@edujalm.fr">report@edujalm.fr</a> .
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 4 }}>
                            7. Suivi des Communications
                        </Typography>
                        <Typography variant="body1" paragraph>
                            L'apprenant est informé que les emails envoyés par la plateforme peuvent faire l'objet d'un suivi (tracking) permettant de confirmer leur ouverture et la bonne réception des informations, ceci afin de justifier de l'assiduité et du bon déroulement du parcours de formation.
                        </Typography>

                        {showAcceptButton && (
                            <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <Divider sx={{ width: '100%', mb: 4 }} />
                                <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                    En cliquant sur le bouton ci-dessous, vous reconnaissez avoir pris connaissance du règlement apprenant et de la politique RGPD, et vous engagez à les respecter.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                    onClick={handleAccept}
                                    disabled={submitting}
                                    sx={{
                                        py: 2,
                                        px: 6,
                                        borderRadius: 3,
                                        fontSize: '1.2rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            boxShadow: '0 15px 25px rgba(0,0,0,0.15)'
                                        }
                                    }}
                                >
                                    J'accepte le règlement
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default LearnerRulesPage;
